import "./serviceDetail.css";

import React, { useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  SERVICE_CATALOG,
  getServiceBySlug,
  getServiceDescription,
  isCabBookingService,
  slugify
} from "./serviceCatalog";
import { getImageForKey } from "./serviceImagery";
import { useAppToast } from "./AppToastProvider";
import SiteHeader from "./SiteHeader";
import SiteFooter from "./SiteFooter";

const PROVIDER_NAMES = [
  "Aarav Mehta",
  "Sunita Rao",
  "Vikram Singh",
  "Fatima Sheikh",
  "Rohan Das",
  "Kavya Iyer"
];

const buildProviders = (serviceName) =>
  PROVIDER_NAMES.slice(0, 4).map((name, index) => ({
    name,
    experience: 2 + index,
    rating: (4.5 + index * 0.1).toFixed(1),
    price: 299 + index * 120,
    tag: index === 0 ? "Top rated" : index === 1 ? "Quick response" : "Verified"
  }));

const ServiceDetailScreen = () => {
  const navigate = useNavigate();
  const toast = useAppToast();
  const { categorySlug, serviceSlug } = useParams();

  const match = useMemo(() => getServiceBySlug(SERVICE_CATALOG, categorySlug, serviceSlug), [categorySlug, serviceSlug]);
  const providers = useMemo(() => (match ? buildProviders(match.service) : []), [match]);

  const [form, setForm] = useState({ name: "", phone: "", note: "" });
  const [selectedProvider, setSelectedProvider] = useState(null);

  if (!match) {
    return <Navigate to="/" replace />;
  }

  const isCabRelated = isCabBookingService(match.category, match.service);
  const heroImage = getImageForKey(`${match.category}-${match.service}`);

  const submitRequest = (event) => {
    event.preventDefault();

    if (!form.name.trim() || !form.phone.trim()) {
      toast.showInfo("Please enter your name and phone number.");
      return;
    }

    toast.showSuccess(`Request submitted for ${match.service}. Providers will reach out shortly.`);
    setForm({ name: "", phone: "", note: "" });
  };

  const approachProvider = (provider) => {
    setSelectedProvider(provider.name);
    toast.showSuccess(`Request sent to ${provider.name} for ${match.service}.`);
  };

  return (
    <div className="sd-page">
      <SiteHeader />

      <div className="sd-subheader">
        <button type="button" className="sd-back-btn" onClick={() => navigate(-1)}>
          <i className="fas fa-arrow-left" aria-hidden="true"></i> Back
        </button>
      </div>

      <main className="sd-main">
        <motion.section
          className="sd-hero"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.32 }}
        >
          <img src={heroImage} alt={match.service} />
          <div className="sd-hero-copy">
            <p className="sd-breadcrumb">
              <Link to={`/services/${slugify(match.category)}`}>{match.category}</Link> / {match.service}
            </p>
            <h1>{match.service}</h1>
            <p>{getServiceDescription(match.category, match.service)}</p>

            {isCabRelated && (
              <button type="button" className="sd-primary-btn" onClick={() => navigate("/cab-booking")}>
                Open Live Cab Booking
              </button>
            )}
          </div>
        </motion.section>

        <section className="sd-grid">
          <div className="sd-providers">
            <h2>Providers ready to help</h2>
            <div className="sd-provider-list">
              {providers.map((provider, index) => (
                <motion.article
                  key={provider.name}
                  className={selectedProvider === provider.name ? "sd-provider-card active" : "sd-provider-card"}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.24, delay: index * 0.05 }}
                >
                  <div className="sd-provider-top">
                    <h3>{provider.name}</h3>
                    <span>{provider.tag}</span>
                  </div>
                  <p className="sd-provider-meta">
                    {provider.experience} yrs experience · ★ {provider.rating} · from ₹{provider.price}
                  </p>
                  <button type="button" className="sd-secondary-btn" onClick={() => approachProvider(provider)}>
                    {selectedProvider === provider.name ? "Request Sent" : "Request This Provider"}
                  </button>
                </motion.article>
              ))}
            </div>
          </div>

          <form className="sd-request-form" onSubmit={submitRequest}>
            <h2>Request {match.service}</h2>
            <p className="sd-form-copy">Share your details once and providers will contact you directly.</p>

            <label>
              <span>Your name</span>
              <input
                type="text"
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              />
            </label>

            <label>
              <span>Phone number</span>
              <input
                type="tel"
                value={form.phone}
                onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))}
              />
            </label>

            <label>
              <span>What do you need?</span>
              <textarea
                rows={3}
                value={form.note}
                onChange={(event) => setForm((prev) => ({ ...prev, note: event.target.value }))}
                placeholder={`Describe your ${match.service.toLowerCase()} requirement`}
              />
            </label>

            <button type="submit" className="sd-primary-btn">
              Submit Request
            </button>
          </form>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default ServiceDetailScreen;
